import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Target, Loader2, Wand2, Users, ArrowRight, X, CheckCircle2, Sparkles,
  Linkedin, Mail, Building2, Upload, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

import ConsoleShell from "@/components/ConsoleShell";
import api from "@/lib/api";

const EXAMPLE_PASTE = `Jean Dupont, Bâtiments Dupont SARL, dirigeant, https://linkedin.com/in/jeandupont
Marie Martin - CSPS Nouvelle-Aquitaine chez Sécuria - marie@securia.fr
Pierre Legrand - Conducteur travaux - Constructions du Sud - Bordeaux`;

const EXAMPLE_TARGETS = [
  "CSPS externes en région Nouvelle-Aquitaine (TPE 2-10 personnes)",
  "Dirigeants TPE BTP < 20 salariés — Île-de-France",
  "Conducteurs de travaux gros œuvre, PACA",
  "Coordonnateurs SPS indépendants",
];

export default function Prospection() {
  const [rawText, setRawText] = useState("");
  const [targetContext, setTargetContext] = useState("");
  const [autoEnrich, setAutoEnrich] = useState(true);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const navigate = useNavigate();

  const parse = async () => {
    if (!rawText.trim()) {
      toast.error("Colle d'abord ta liste de prospects");
      return;
    }
    setParsing(true);
    setImportResult(null);
    try {
      const { data } = await api.post("/prospects/parse-text", { raw_text: rawText });
      setParsed(data.parsed || []);
      if (!data.parsed?.length) {
        toast.error("Clara n'a pas trouvé de prospect exploitable dans ce texte");
      } else {
        toast.success(`Clara a extrait ${data.count} prospect${data.count > 1 ? "s" : ""}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse échouée");
    } finally {
      setParsing(false);
    }
  };

  const updateRow = (i, field, val) => {
    setParsed((arr) => arr.map((p, idx) => (idx === i ? { ...p, [field]: val } : p)));
  };
  const removeRow = (i) => {
    setParsed((arr) => arr.filter((_, idx) => idx !== i));
  };

  const runImport = async () => {
    if (!parsed.length) return;
    setImporting(true);
    try {
      const { data } = await api.post(
        "/prospects/bulk-import",
        {
          prospects: parsed,
          target_context: targetContext,
          auto_enrich: autoEnrich,
        },
        { timeout: 240000 },
      );
      setImportResult(data);
      toast.success(`✅ ${data.imported} prospect${data.imported > 1 ? "s" : ""} importé${data.imported > 1 ? "s" : ""} — ${data.enriched} enrichi${data.enriched > 1 ? "s" : ""} par Clara`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import échoué");
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setRawText("");
    setTargetContext("");
    setParsed([]);
    setImportResult(null);
  };

  return (
    <ConsoleShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="prospection-page">
        {/* Header */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
            Clara · Moteur de prospection
          </div>
          <h1 className="text-3xl sm:text-4xl font-light text-white tracking-tight">
            Prospection IA en batch
          </h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
            Colle une liste de prospects (LinkedIn, Pappers, tableur, n&apos;importe où). Clara les
            structure et rédige pour chacun un pack outreach complet : invite LinkedIn + relance +
            cold email personnalisés.
          </p>
        </div>

        {importResult ? (
          <ImportResult result={importResult} onReset={reset} onGoProspects={() => navigate("/app/prospects")} />
        ) : (
          <>
            {/* Step 1 — target */}
            <div className="border border-white/5 bg-white/[0.02] p-4 sm:p-6 space-y-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-6 h-6 border border-white/20 text-xs text-zinc-300">1</span>
                <div className="text-sm text-white font-medium">Contexte de la campagne</div>
              </div>
              <textarea
                value={targetContext}
                onChange={(e) => setTargetContext(e.target.value)}
                placeholder="Ex. : CSPS externes en région Nouvelle-Aquitaine (TPE 2-10 personnes), on cherche à recruter 5 pilotes pour tester Chantix"
                rows={2}
                data-testid="target-context-input"
                className="w-full bg-black/40 border border-white/10 text-sm text-white p-3 focus:outline-none focus:border-white/30 resize-none placeholder:text-zinc-600"
              />
              <div className="flex flex-wrap gap-1.5">
                {EXAMPLE_TARGETS.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTargetContext(t)}
                    className="text-[10px] px-2 py-1 border border-white/10 text-zinc-500 hover:text-white hover:border-white/20 transition"
                  >
                    + {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2 — paste */}
            <div className="border border-white/5 bg-white/[0.02] p-4 sm:p-6 space-y-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-6 h-6 border border-white/20 text-xs text-zinc-300">2</span>
                <div className="text-sm text-white font-medium">Colle ta liste de prospects</div>
              </div>
              <p className="text-xs text-zinc-500">
                Format libre — une ligne par prospect. Clara comprend tous les formats :
                CSV, listing LinkedIn, notes, tableur, etc.
              </p>
              <textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder={EXAMPLE_PASTE}
                rows={8}
                data-testid="raw-text-input"
                className="w-full bg-black/40 border border-white/10 text-sm text-white p-3 focus:outline-none focus:border-white/30 resize-none placeholder:text-zinc-700 font-mono"
              />
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={parse}
                  disabled={parsing || !rawText.trim()}
                  data-testid="parse-btn"
                  className="inline-flex items-center gap-2 bg-white text-black px-4 py-2 text-sm font-medium hover:bg-zinc-200 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {parsing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Clara analyse…
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      Analyser la liste
                    </>
                  )}
                </button>
                <button
                  onClick={() => setRawText(EXAMPLE_PASTE)}
                  className="text-xs text-zinc-500 hover:text-white transition"
                >
                  Charger l&apos;exemple
                </button>
              </div>
            </div>

            {/* Step 3 — preview + import */}
            {parsed.length > 0 && (
              <div className="border border-white/5 bg-white/[0.02] p-4 sm:p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 border border-white/20 text-xs text-zinc-300">3</span>
                  <div className="text-sm text-white font-medium">
                    Vérifie et lance l&apos;import ({parsed.length} prospect{parsed.length > 1 ? "s" : ""})
                  </div>
                </div>

                <div className="border border-white/5 overflow-hidden">
                  <div className="max-h-[420px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-white/[0.02] text-zinc-500 uppercase tracking-wider sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-normal">Nom</th>
                          <th className="text-left px-3 py-2 font-normal">Société</th>
                          <th className="text-left px-3 py-2 font-normal">Rôle</th>
                          <th className="text-left px-3 py-2 font-normal">LinkedIn</th>
                          <th className="text-left px-3 py-2 font-normal">Email</th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsed.map((p, i) => (
                          <tr key={i} className="border-t border-white/5 hover:bg-white/[0.02]">
                            <td className="px-3 py-2"><EditCell value={p.full_name} onChange={(v) => updateRow(i, "full_name", v)} /></td>
                            <td className="px-3 py-2"><EditCell value={p.company} onChange={(v) => updateRow(i, "company", v)} /></td>
                            <td className="px-3 py-2"><EditCell value={p.role} onChange={(v) => updateRow(i, "role", v)} /></td>
                            <td className="px-3 py-2"><EditCell value={p.linkedin_url} onChange={(v) => updateRow(i, "linkedin_url", v)} placeholder="—" /></td>
                            <td className="px-3 py-2"><EditCell value={p.email} onChange={(v) => updateRow(i, "email", v)} placeholder="—" /></td>
                            <td className="px-2">
                              <button onClick={() => removeRow(i)} className="text-zinc-600 hover:text-red-400 p-1" aria-label="Retirer">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-300">
                  <input
                    type="checkbox"
                    checked={autoEnrich}
                    onChange={(e) => setAutoEnrich(e.target.checked)}
                    data-testid="auto-enrich-toggle"
                    className="w-4 h-4"
                  />
                  <Sparkles className="w-3.5 h-3.5 text-pink-400" />
                  <span>
                    Générer automatiquement le pack outreach pour chaque prospect
                    <span className="text-zinc-500"> (invite LinkedIn + relance + cold email)</span>
                  </span>
                </label>

                {autoEnrich && parsed.length > 10 && (
                  <div className="flex items-start gap-2 text-xs text-amber-300/80 bg-amber-500/5 border border-amber-500/20 p-2">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>{parsed.length} prospects × 3 assets — la génération prendra 30-90 secondes. Reste sur cette page.</span>
                  </div>
                )}

                <button
                  onClick={runImport}
                  disabled={importing || !parsed.length}
                  data-testid="import-btn"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-emerald-500 text-black px-6 py-3 text-sm font-medium hover:bg-emerald-400 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Import + enrichissement en cours…
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Importer {parsed.length} prospect{parsed.length > 1 ? "s" : ""} {autoEnrich && "+ pack outreach"}
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </ConsoleShell>
  );
}

function EditCell({ value, onChange, placeholder }) {
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-transparent border-b border-transparent hover:border-white/10 focus:border-white/30 focus:outline-none text-zinc-200 placeholder:text-zinc-700 text-xs px-1 py-0.5"
    />
  );
}

function ImportResult({ result, onReset, onGoProspects }) {
  const withOutreach = (result.prospects || []).filter((p) => p.outreach);
  return (
    <div className="border border-emerald-500/20 bg-emerald-500/[0.03] p-6 space-y-6" data-testid="import-result">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="w-6 h-6 text-emerald-400 mt-0.5" />
        <div>
          <div className="text-lg text-white font-medium">Import terminé</div>
          <div className="text-sm text-zinc-400 mt-1">
            <span className="text-emerald-300 font-mono">{result.imported}</span> prospect{result.imported > 1 ? "s" : ""} ajouté{result.imported > 1 ? "s" : ""} · <span className="text-emerald-300 font-mono">{result.enriched}</span> enrichi{result.enriched > 1 ? "s" : ""} avec le pack outreach.
          </div>
        </div>
      </div>

      {withOutreach.length > 0 && (
        <div className="space-y-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Aperçu — premier pack outreach</div>
          <OutreachPreview p={withOutreach[0]} />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 pt-2">
        <button
          onClick={onGoProspects}
          data-testid="go-prospects-btn"
          className="inline-flex items-center justify-center gap-2 bg-white text-black px-5 py-2.5 text-sm font-medium hover:bg-zinc-200 transition"
        >
          <Users className="w-4 h-4" />
          Voir tous les prospects
          <ArrowRight className="w-4 h-4" />
        </button>
        <button
          onClick={onReset}
          className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-5 py-2.5 text-sm hover:bg-white/5 transition"
        >
          <Target className="w-4 h-4" />
          Lancer une autre campagne
        </button>
      </div>
    </div>
  );
}

function OutreachPreview({ p }) {
  return (
    <div className="border border-white/5 bg-black/40 p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm text-white">
        <Building2 className="w-4 h-4 text-zinc-500" />
        <span>{p.full_name}</span>
        {p.company && <span className="text-zinc-500">· {p.company}</span>}
      </div>
      {p.outreach?.linkedin_invite && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-blue-400 mb-1 flex items-center gap-1">
            <Linkedin className="w-3 h-3" /> Invite LinkedIn
          </div>
          <div className="text-xs text-zinc-200 whitespace-pre-wrap leading-relaxed">
            {p.outreach.linkedin_invite}
          </div>
        </div>
      )}
      {p.outreach?.cold_email && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 mb-1 flex items-center gap-1">
            <Mail className="w-3 h-3" /> Cold email
          </div>
          <div className="text-xs text-zinc-200 whitespace-pre-wrap leading-relaxed">
            {p.outreach.cold_email}
          </div>
        </div>
      )}
    </div>
  );
}
